import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { constants } from '../../constant/constant';

const formatDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  if (isNaN(dt)) return d;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(dt.getMonth() + 1)}/${pad(dt.getDate())}/${dt.getFullYear()}`;
};

const computeEndDate = (subAccount, subscription) => {
  // Return stored end_on if already set
  if (subAccount.end_on) return new Date(subAccount.end_on);

  const startOn = subAccount.start_on ? new Date(subAccount.start_on) : new Date();

  // Free trial: start_on + trial_days
  if (subscription?.is_free_trail && subscription?.trial_days > 0) {
    const d = new Date(startOn);
    d.setDate(d.getDate() + Number(subscription.trial_days));
    return d;
  }

  // Paid subscription: start_on + duration (months)
  if (subscription?.duration > 0) {
    const d = new Date(startOn);
    d.setMonth(d.getMonth() + Number(subscription.duration));
    return d;
  }

  return null;
};

const ThankYouPage = () => {
  const [loading, setLoading] = useState(false);
  const [subAccount, setSubAccount] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [account, setAccount] = useState({ name: '', email: '', orderNo: '' });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const accountName = params.get('accountName');
    const accountEmail = params.get('accountEmail');
    const orderNo = params.get('orderNo') || params.get('order_no');

    setAccount({ name: accountName || '', email: accountEmail || '', orderNo: orderNo || '' });

    if (!sessionId) return;

    setLoading(true);
    const url = `${constants.ADMIN_URL}${constants.API.CHECKOUT_SUBSCRIPTION_DETAILS}?session_id=${encodeURIComponent(sessionId)}`;
    axios.get(url, {
      headers: { 'Content-Type': 'application/json', 'Cache-Control': 'no-cache' },
    })
      .then((res) => {
        const data = res?.data?.response;
        const sa = data?.subscription_account || null;
        setSubAccount(sa);
        setSubscription(data?.subscription || null);

        const resolvedName =
          data?.account?.name ||
          data?.account?.account_name ||
          data?.account_name ||
          sa?.account?.name ||
          sa?.account_name ||
          sa?.name ||
          '';

        const resolvedEmail =
          data?.account?.email ||
          sa?.account?.email ||
          sa?.email ||
          '';

        setAccount(prev => ({
          name: prev.name || resolvedName,
          email: prev.email || resolvedEmail,
          orderNo: prev.orderNo || (data?.order_no != null ? String(data.order_no) : ''),
        }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const endDate = subAccount ? computeEndDate(subAccount, subscription) : null;

  const remainingDays = (() => {
    if (!endDate) return null;
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const end = new Date(endDate);
    end.setHours(0, 0, 0, 0);
    return Math.ceil((end - now) / (1000 * 60 * 60 * 24));
  })();

  const remainingDaysValue = (() => {
    if (remainingDays === null) return null;
    if (remainingDays < 0) return <span style={{ color: '#f44336', fontWeight: 600 }}>Expired</span>;
    if (remainingDays === 0) return <span style={{ color: '#f44336', fontWeight: 600 }}>Expires Today</span>;
    if (remainingDays <= 10) return <span style={{ color: '#f44336', fontWeight: 600 }}>⚠ {remainingDays} day{remainingDays !== 1 ? 's' : ''} remaining</span>;
    return <span style={{ color: '#4caf50', fontWeight: 600 }}>{remainingDays} days</span>;
  })();

  const rows = subAccount
    ? [
        { label: 'Account Name', value: account?.name || subAccount?.account?.name || subAccount?.account?.account_name || subAccount?.account_name || subAccount?.name || '-' },
        { label: 'Order No', value: account?.orderNo || '-' },
        ...(account?.email ? [{ label: 'Email', value: account.email }] : []),
        { label: 'Subscription Plan', value: subscription?.name || '-' },
        { label: 'Amount', value: subAccount.price != null ? `$${Number(subAccount.price).toFixed(2)}` : '-' },
        { label: 'Start Date', value: formatDate(subAccount.start_on) },
        { label: 'End Date', value: formatDate(endDate) },
        ...(remainingDaysValue ? [{ label: 'Remaining Days', value: remainingDaysValue }] : []),
        { label: 'Recurring', value: subAccount.is_recurring ? 'Yes' : 'No' },
        ...(subAccount.is_free_trail
          ? [{ label: 'Free Trial', value: `${subAccount.trial_days} day${subAccount.trial_days !== 1 ? 's' : ''}` }]
          : []),
      ]
    : [];

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        {/* Success icon */}
        <div style={styles.iconWrap}>
          <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
            <circle cx="28" cy="28" r="28" fill="#263e2f" />
            <path
              d="M16 28.5L23.5 36L40 20"
              stroke="#4caf50"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Headline */}
        <h1 style={styles.title}>Thank You!</h1>
        <p style={styles.subtitle}>Your payment was successful. Your subscription is now active.</p>

        {/* Subscription details */}
        {loading ? (
          <p style={styles.loadingText}>Loading subscription details…</p>
        ) : rows.length > 0 ? (
          <div style={styles.detailsCard}>
            <div style={styles.detailsHeader}>
              <span style={styles.detailsHeaderText}>Subscription Details</span>
              <span style={styles.statusBadge}>Completed</span>
            </div>
            <div style={styles.rowList}>
              {rows.map(({ label, value }) => (
                <div key={label} style={styles.row}>
                  <span style={styles.rowLabel}>{label}</span>
                  <span style={styles.rowValue}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* Action buttons */}
        <div style={styles.actions}>
          <a href="/login" style={styles.btnSecondary}>
            Home
          </a>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0f1923 0%, #1e2a35 100%)',
    padding: '24px 16px',
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
  },
  card: {
    background: '#1e2a35',
    borderRadius: 16,
    padding: '40px 36px',
    maxWidth: 480,
    width: '100%',
    boxShadow: '0 8px 40px rgba(0,0,0,0.45)',
    textAlign: 'center',
  },
  iconWrap: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 20,
  },
  title: {
    color: '#ffffff',
    fontSize: 28,
    fontWeight: 700,
    margin: '0 0 8px',
  },
  subtitle: {
    color: '#90a4ae',
    fontSize: 14,
    lineHeight: 1.6,
    margin: '0 0 28px',
  },
  loadingText: {
    color: '#78909c',
    fontSize: 13,
    marginBottom: 28,
  },
  detailsCard: {
    background: '#263340',
    borderRadius: 10,
    padding: '16px 20px',
    marginBottom: 28,
    textAlign: 'left',
  },
  detailsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
    paddingBottom: 12,
    borderBottom: '1px solid #37474f',
  },
  detailsHeaderText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 600,
  },
  statusBadge: {
    color: '#4caf50',
    background: '#263e2f',
    borderRadius: 12,
    padding: '2px 12px',
    fontSize: 11,
    fontWeight: 600,
  },
  rowList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  rowLabel: {
    color: '#90a4ae',
    fontSize: 13,
  },
  rowValue: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: 500,
  },
  actions: {
    display: 'flex',
    gap: 12,
    justifyContent: 'center',
  },
  btnSecondary: {
    display: 'inline-block',
    background: 'transparent',
    color: '#90a4ae',
    textDecoration: 'none',
    borderRadius: 8,
    padding: '10px 24px',
    fontSize: 14,
    fontWeight: 500,
    border: '1px solid #37474f',
    transition: 'border-color 0.2s',
  },
};

export default ThankYouPage;
